import {
  computed,
  ComputedRef,
  ref,
  Ref,
  useAsync,
  useContext,
  useRoute,
  useRouter,
  useStore,
  watch,
} from '@nuxtjs/composition-api';
import { Hit } from '@algolia/client-search';
import aa, { InsightsEvent } from 'search-insights';
import { v4 } from 'uuid';
import axios, { AxiosResponse } from 'axios';
import { SearchResponseWithFacets, Facets } from './types';
import {
  formatActiveFilters,
  getFacetsWithZeroValueThatHaveFilters,
  sortFacetsBasedOnKey,
  deleteFacetValues,
  sortFormattedFacetValues,
  filterFormattedFacetValues,
} from './utils';
import { UseFilters, useFilters, Filters } from './useFilters';
import { Sort, DEFAULT_SORT } from '~/types/sort';
import { State } from '~/store/index';
import { AlgoliaResource } from '~/types/resource';
import { chunk } from '~/utils/objects';
import { AlgoliaResponse, CacheResponse } from '~/server_middleware/search/types';
import { FormattedFacet } from '~/types/facets';

type SearchAPIResponse = AlgoliaResponse<AlgoliaResource> | CacheResponse<AlgoliaResource>;
export type SearchResults =
  | (SearchResponseWithFacets & { cacheHit: boolean; showNoResultsPage?: boolean })
  | null;
interface UseSearch {
  query: Ref<string>;
  isSearching: Ref<boolean>;
  sort: Ref<Sort>;
  page: Ref<number>;
  results: Ref<SearchResults>;
  facets: ComputedRef<Facets>;
  formatFacets: ComputedRef<FormattedFacet[]>;
  showNoResultsPage: ComputedRef<boolean>;
  filters: UseFilters['filters'];
  sendAlgoliaClickEvent: (hit: Hit<AlgoliaResource>, position: number) => void;
  addFilter: UseFilters['add'];
  removeFilter: UseFilters['remove'];
  clearFilters: UseFilters['clear'];
}

interface UseSearchOptions {
  page?: number;
  filters?: Filters;
  sort?: Sort;
}

interface SearchParams {
  query: string;
  filters: Filters;
  sort: Sort;
  page: number;
  locale: string;
}

/**
 * Note: a facet is a category that we want to filter by. For example, we might want to filter by learning area, year level, resource type, etc. This is what
 * we use to populate the filters on the search page. A filter is more like a query that we can supply to algolia. At the moment we only use country but
 * this could include other things like number of downloads, etc.
 */
export const useSearch = (initialQuery: string, options?: UseSearchOptions): UseSearch => {
  const {
    filters,
    add: addFilter,
    remove: removeFilter,
    clear: clearFilters,
  } = useFilters(options?.filters);
  const { $config, $isFlagOn, $axios, $cookies, i18n, localePath } = useContext();
  const store = useStore<State>();
  const user = store.state?.auth?.user?.id;
  const deviceId = $cookies.get('deviceId') || store.getters.deviceId;
  const router = useRouter();
  const route = useRoute();
  const location = computed(() => store.state.location);
  const locale = computed(() => location.value.secondary);
  const query = ref(initialQuery);
  const sort = ref<Sort>(options?.sort || DEFAULT_SORT);
  const page = ref(options?.page || 0);
  const isSearching = ref(false);
  const showNoResultsPage = computed(() => results.value?.showNoResultsPage || false);

  aa('setUserToken', user ? String(user) : `anonymous-${deviceId || v4()}`);

  const getIndexName = (sort: Sort | null): string => {
    return sort
      ? sort === 'recent'
        ? $config.algolia.resourceSortDateIndex
        : $config.algolia.resourceSortPopularityIndex
      : $config.algolia.resourceIndex;
  };

  // use this for populating the filters on the search page
  const facets = computed(() =>
    results.value?.facets ? deleteFacetValues(sortFacetsBasedOnKey(results.value?.facets)) : {}
  );

  const formatFacets = computed<FormattedFacet[]>(() =>
    results.value?.formattedFacets
      ? sortFormattedFacetValues(filterFormattedFacetValues(results.value?.formattedFacets || []))
      : []
  );
  const searchAPI = async (params: SearchParams): Promise<AxiosResponse<SearchAPIResponse>> => {
    const { query, filters, sort, page, locale } = params;
    const filtersAsArray = Object.entries(filters).map(([key, values]) => ({
      key,
      values,
    }));

    return await $axios.post<SearchAPIResponse>(
      '/fn/search',
      {
        query,
        filters: filtersAsArray,
        sort,
        page,
        locale,
        userToken: user ? String(user) : `anonymous-${deviceId || v4()}`,
        useCache: $isFlagOn('cache-search'),
      },
      {
        baseURL: $config.baseURL,
      }
    );
  };

  const sendViewEvents = (hits: Hit<AlgoliaResource>[], cached: boolean): void => {
    // split searchResponse.hits into chunks of 20
    const chunkedHits = chunk(hits, 20);
    // send a search event for each chunk. Can only send 20 at a time
    const events: InsightsEvent[] = chunkedHits.map(chunk => {
      return {
        index: getIndexName(sort.value),
        eventType: 'view',
        eventName: cached ? 'Viewed cached search results' : 'Viewed search results',
        objectIDs: chunk.map(hit => hit.objectID),
      };
    });
    aa('sendEvents', events);
  };

  const search = async (): Promise<SearchResults> => {
    isSearching.value = true;
    try {
      const { data } = await searchAPI({
        query: query.value,
        filters: filters.value,
        sort: sort.value,
        page: page.value,
        locale: locale.value,
      });

      const searchResponse = { ...data.results, cacheHit: !!data.cached, showNoResultsPage: false };

      // If there are no results and no filters, show the the no results message, but also our empty search results
      if (!searchResponse.hits?.length && (!filters.value || !Object.keys(filters.value).length)) {
        const noResultsSearch = await searchAPI({
          query: '',
          filters: {},
          sort: DEFAULT_SORT,
          page: 0,
          locale: locale.value,
        });
        return {
          ...noResultsSearch.data.results,
          showNoResultsPage: true,
          cacheHit: !!data.cached,
        };
      }

      if (filters.value) {
        // add a 0 value for each facet that doesn't have any results but has previously been selected by the user
        const facetsWithZeroValues = getFacetsWithZeroValueThatHaveFilters(
          searchResponse.facets,
          filters.value
        );
        searchResponse.facets = facetsWithZeroValues;
      }

      if (searchResponse.hits?.length) {
        sendViewEvents(searchResponse.hits, !!data.cached);
      }
      return searchResponse;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.warn(
          'Sent request to search API but got an error',
          error.config.url,
          error.config.data
        );
        console.error(error.message, error.response?.data);
      } else {
        console.error(error);
      }
      return null;
    } finally {
      isSearching.value = false;
    }
  };

  const sendAlgoliaClickEvent = (item: Hit<AlgoliaResource>, position: number): void => {
    if (!item?.objectID) {
      return;
    }
    if (results.value?.queryID) {
      aa('clickedObjectIDsAfterSearch', {
        eventName: 'Search Result Clicked',
        index: getIndexName(sort.value),
        objectIDs: [item.objectID],
        positions: [position],
        queryID: results.value?.queryID,
      });
    } else {
      aa('clickedObjectIDs', {
        eventName: 'Search Result Clicked',
        index: getIndexName(sort.value),
        objectIDs: [item.objectID],
      });
    }
  };

  watch(
    [query, filters, sort, page],
    async () => {
      results.value = await search();
    },
    { deep: true }
  );

  watch([filters, sort], ([newFilters, newSort]) => {
    // if on the no results page and the user changes the filters, clear the query
    if (results && results.value?.showNoResultsPage) {
      query.value = '';
    }
    router.replace({
      // this is set because sometimes the router updates after i18n and can cause a redirect
      // back to the country a user is switching from
      path: localePath(`/search/${encodeURIComponent(query.value)}`),
      query: {
        ...newFilters,
        sort: newSort === DEFAULT_SORT ? undefined : newSort,
        // keep ?query= if it exists
        query: route.value?.query?.query ? route.value.query.query : undefined,
      },
    });
  });

  watch(location, async () => {
    await i18n.waitForPendingLocaleChange();
    router.onReady(() => clearFilters());
  });

  const results = useAsync(
    () => search(),
    `search-${locale.value}-${query.value}-${JSON.stringify(filters.value)}`
  );

  return {
    query,
    isSearching,
    sort,
    page,
    results,
    facets,
    formatFacets,
    filters,
    showNoResultsPage,
    sendAlgoliaClickEvent,
    addFilter,
    removeFilter,
    clearFilters,
  };
};

export * from './types';
export * from './utils';
